import { useEffect, useRef } from "react";
import { useAppContext } from "../../contexts/AppContext";
import SearchIcon from "../../icons/search.svg?react";
import ClearIcon from "../../icons/clear.svg?react";
import CircleAnimated from "../../icons/circle-anim.svg?react";
import { css } from "@emotion/react";

function SearchClearIcon() {
  const { searchValue = "", dispatch } = useAppContext();
  if (!searchValue) {
    return <SearchIcon className="text-neutral-900" aria-label="Search" />;
  }
  return (
    <button
      aria-label="Clear"
      className="-mb-[1px]"
      onClick={() => dispatch("update", { searchValue: "" })}
    >
      <ClearIcon className="text-neutral-900 hover:text-neutral-500 h-[1.1rem] w-[1.1rem]" />
    </button>
  );
}

function Loading() {
  const { indexComplete, searchValue } = useAppContext();
  if (!indexComplete && searchValue) {
    return <CircleAnimated className="shrink-0" />;
  }
  return null;
}

function CancelButton() {
  const { dispatch } = useAppContext();
  return (
    <button
      className="ml-3 text-sm text-neutral-500 sm:hidden"
      aria-label="Cancel"
      onClick={() => dispatch("update", { showPopup: false })}
    >
      Cancel
    </button>
  );
}

export default function SearchBox() {
  const { searchValue, dispatch } = useAppContext();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => inputRef?.current?.focus(), 150);

    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        dispatch("update", { showPopup: false });
      }
    };

    const node = containerRef?.current;
    node?.ownerDocument.addEventListener("keyup", handleKeyUp);

    return () => {
      clearTimeout(timer);
      node?.ownerDocument.removeEventListener("keyup", handleKeyUp);
    };
  }, [dispatch]);

  const className = searchValue
    ? "z-10 relative flex items-center py-5 px-4 sm:px-7 bg-white rounded-t-lg shadow"
    : "z-10 relative flex items-center py-5 px-4 sm:px-7 bg-white rounded-lg";

  return (
    <div
      className={className}
      ref={containerRef}
      css={css`
        width: 512px;

        @media (max-width: 540px) {
          width: 400px;
        }
        @media (max-width: 440px) {
          width: 100%;
        }
      `}
    >
      <div className="flex items-center justify-center w-4 h-4 mr-3">
        <SearchClearIcon />
      </div>
      <input
        ref={inputRef}
        value={searchValue || ""}
        onChange={(e) => dispatch("update", { searchValue: e.target.value })}
        onKeyDown={(e) => {
          if (e.key === "ArrowUp" || e.key === "ArrowDown") {
            e.preventDefault();
          }
        }}
        css={css`
          padding-left: 12px;
        `}
        className="grow -my-5 py-5 -ml-3 pl-3 text-[1.65rem] focus-visible:outline-none placeholder:text-gray-400 outline-none truncate"
        placeholder="Search posts, tags and authors"
      />
      <Loading />
      <CancelButton />
    </div>
  );
}
